import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { StatsCards } from '../../components/StatsCards';
import { FilterBar } from '../../components/FilterBar';
import { eventService, Event } from '../../services/eventService';
import api, { toMediaUrl } from '../../config/api';
import { Comment } from '../../services/eventService';

export const AdminEvents: React.FC = () => {
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState<any>({ lieu: 'Mosquée Université de Thiès' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    a_venir: 0,
    en_cours: 0,
    termine: 0,
  });
  const [sectionStats, setSectionStats] = useState<{ VCN: number; HDR: number; SANTE: number }>({
    VCN: 0,
    HDR: 0,
    SANTE: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    section: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);

  useEffect(() => {
    fetchEvents(1);
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allEvents, currentPage]);

  const fetchStats = async () => {
    try {
      const allData = await eventService.getAll({ page_size: 1000 });
      const allEvents = allData.results || [];
      
      const statsData = {
        total: allData.count || allEvents.length,
        a_venir: allEvents.filter((e: Event) => e.status_auto === 'a_venir').length,
        en_cours: allEvents.filter((e: Event) => e.status_auto === 'en_cours').length,
        termine: allEvents.filter((e: Event) => e.status_auto === 'termine').length,
      };
      setStats(statsData);

      const sectionCounts = { VCN: 0, HDR: 0, SANTE: 0 };
      allEvents.forEach((event: Event) => {
        if (event.section === 'VCN') {
          sectionCounts.VCN += 1;
        } else if (event.section === 'HDR') {
          sectionCounts.HDR += 1;
        } else if (event.section === 'SANTE') {
          sectionCounts.SANTE += 1;
        }
      });
      setSectionStats(sectionCounts);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const fetchEvents = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await eventService.getAll({ page_size: 1000 });
      const allEventsData = data.results || [];
      setAllEvents(allEventsData);
      setTotalCount(allEventsData.length);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allEvents];

    // Filtre par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((event) =>
        event.titre?.toLowerCase().includes(searchLower) ||
        event.description?.toLowerCase().includes(searchLower) ||
        event.lieu?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par type
    if (filters.type) {
      filtered = filtered.filter((event) => event.type_event === filters.type);
    }

    // Filtre par section
    if (filters.section) {
      filtered = filtered.filter((event) => event.section === filters.section);
    }

    // Filtre par statut
    if (filters.status) {
      if (filters.status === 'a_venir') {
        filtered = filtered.filter((event) => event.status_auto === 'a_venir');
      } else if (filters.status === 'en_cours') {
        filtered = filtered.filter((event) => event.status_auto === 'en_cours');
      } else if (filters.status === 'termine') {
        filtered = filtered.filter((event) => event.status_auto === 'termine');
      }
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || a.date_event || 0).getTime();
      const dateB = new Date((b as any).created_at || b.date_event || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 6;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedEvents = filtered.slice(startIndex, endIndex);

    setEvents(paginatedEvents);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleCreate = () => {
    setFormData({ section: 'VCN', lieu: 'Mosquée Université de Thiès' });
    setSelectedEvent(null);
    setIsModalOpen(true);
  };

  const handleEdit = (event: Event) => {
    setFormData({
      titre: event.titre,
      description: event.description,
      type_event: event.type_event,
      section: event.section || 'VCN',
      date_event: event.date_event.split('T')[0],
      heure_debut: event.heure_debut,
      heure_fin: event.heure_fin || '',
      lieu: event.lieu,
    });
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleView = (event: Event) => {
    setSelectedEvent(event);
    setIsViewModalOpen(true);
    // Charger les commentaires
    loadComments(event.id);
  };

  const loadComments = async (eventId: number) => {
    setLoadingComments(true);
    try {
      const commentsData = await eventService.getComments(eventId);
      setComments(commentsData);
    } catch (error) {
      console.error('Erreur lors du chargement des commentaires:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleDelete = (event: Event) => {
    setSelectedEvent(event);
    setIsDeleteModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // Empêcher les doubles clics
    
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedEvent) {
        await api.put(`/events/events/${selectedEvent.id}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/events/events/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setIsModalOpen(false);
      await fetchEvents(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedEvent || isDeleting) return;
    setIsDeleting(true);
    try {
      await api.delete(`/events/events/${selectedEvent.id}/`);
      setIsDeleteModalOpen(false);
      await fetchEvents(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    } finally {
      setIsDeleting(false);
    }
  };

  const getTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      balayage: '🧹 Balayage',
      reunion: '🤝 Réunion',
      dahira: '📅 Événement Dahira',
      autre: '📌 Autre',
    };
    return types[type] || type;
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'titre', 
      label: 'Titre',
      render: (value: string) => <strong className="text-green-700">{value}</strong>
    },
    {
      key: 'type_event',
      label: 'Type',
      render: (value: string) => (
        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
          {getTypeLabel(value)}
        </span>
      )
    },
    {
      key: 'section',
      label: 'Section',
      render: (value: string) => {
        const sections: { [key: string]: string } = {
          VCN: 'VCN (Université)',
          HDR: 'HDR',
          SANTE: 'SANTÉ',
        };
        return (
          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
            {sections[value] || value}
          </span>
        );
      }
    },
    {
      key: 'date_event',
      label: 'Date',
      render: (value: string) => (
        <span className="text-gray-600">{new Date(value).toLocaleDateString('fr-FR')}</span>
      )
    },
    {
      key: 'lieu',
      label: 'Lieu'
    },
  ];

  if (loading) {
    return (
      <AdminLayout activeSection="events">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="events">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-green-700">Gestion des Événements</h2>
          <p className="text-gray-600 mt-1">Créez et gérez tous les événements</p>
        </div>
        <button onClick={handleCreate} className="btn-primary text-lg px-6 py-3">
          + Nouvel Événement
        </button>
      </div>

      {/* Cartes de statistiques */}
      <StatsCards
        stats={[
          { label: 'Total Événements', value: stats.total, icon: '📅', color: 'green' },
          { label: 'À venir', value: stats.a_venir, icon: '⏳', color: 'amber' },
          { label: 'En cours', value: stats.en_cours, icon: '🟢', color: 'teal' },
          { label: 'Terminés', value: stats.termine, icon: '✅', color: 'indigo' },
        ]}
      />
      <StatsCards
        stats={[
          { label: 'Section VCN', value: sectionStats.VCN, icon: '🎓', color: 'blue' },
          { label: 'Section HDR', value: sectionStats.HDR, icon: '🏛️', color: 'purple' },
          { label: 'Section SANTÉ', value: sectionStats.SANTE, icon: '🩺', color: 'teal' },
        ]}
      />

      {/* Filtres */}
      <FilterBar
        searchPlaceholder="🔍 Rechercher par titre, description, lieu..."
        searchValue={filters.search}
        onSearchChange={(value) => {
          setFilters({ ...filters, search: value });
          setCurrentPage(1);
        }}
        filters={[
          {
            key: 'type',
            label: 'Tous les types',
            value: filters.type,
            onChange: (value) => {
              setFilters({ ...filters, type: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'balayage', label: '🧹 Balayage' },
              { value: 'reunion', label: '🤝 Réunion' },
              { value: 'dahira', label: '📅 Événement Dahira' },
              { value: 'autre', label: '📌 Autre' },
            ],
          },
          {
            key: 'section',
            label: 'Toutes les sections',
            value: filters.section,
            onChange: (value) => {
              setFilters({ ...filters, section: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'VCN', label: '🎓 VCN (Université)' },
              { value: 'HDR', label: '🏛️ HDR' },
              { value: 'SANTE', label: '🩺 SANTÉ' },
            ],
          },
          {
            key: 'status',
            label: 'Tous les statuts',
            value: filters.status,
            onChange: (value) => {
              setFilters({ ...filters, status: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'a_venir', label: '⏳ À venir' },
              { value: 'en_cours', label: '🟢 En cours' },
              { value: 'termine', label: '✅ Terminés' },
            ],
          },
        ]}
        onClear={() => {
          setFilters({ search: '', type: '', status: '', section: '' });
          setCurrentPage(1);
        }}
      />

      <Table
        columns={columns}
        data={events}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        loading={loading}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
        size="lg"
        footer={
          <div className="flex gap-4">
            <button 
              onClick={() => setIsModalOpen(false)} 
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button 
              onClick={handleSubmit} 
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
            <input
              type="text"
              className="input-field"
              value={formData.titre || ''}
              onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
              required
            />
          </div>

          {/* Photo de l'événement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
            <input
              type="file"
              accept="image/*"
              className="input-field"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setFormData({ ...formData, photo: e.target.files[0] });
                }
              }}
            />
            {/* Aperçu */}
            <div className="mt-3">
              {formData.photo instanceof File ? (
                <img
                  src={URL.createObjectURL(formData.photo)}
                  alt="Aperçu"
                  className="w-full max-w-md h-48 object-cover rounded-lg border"
                />
              ) : selectedEvent?.photo ? (
                <img
                  src={toMediaUrl(selectedEvent.photo)}
                  alt={selectedEvent.titre}
                  className="w-full max-w-md h-48 object-cover rounded-lg border"
                />
              ) : null}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
            <textarea
              className="input-field min-h-[120px]"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
            <select
              className="input-field"
              value={formData.type_event || ''}
              onChange={(e) => setFormData({ ...formData, type_event: e.target.value })}
              required
            >
              <option value="">Sélectionner un type</option>
              <option value="balayage">🧹 Balayage</option>
              <option value="reunion">🤝 Réunion</option>
              <option value="dahira">📅 Événement Dahira</option>
              <option value="autre">📌 Autre</option>
            </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Hanafia *</label>
              <select
                className="input-field"
                value={formData.section || 'VCN'}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                required
              >
                <option value="VCN">VCN (Université)</option>
                <option value="HDR">HDR</option>
                <option value="SANTE">SANTÉ</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
              <input
                type="date"
                className="input-field"
                value={formData.date_event || ''}
                onChange={(e) => setFormData({ ...formData, date_event: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heure de début *</label>
              <input
                type="time"
                className="input-field"
                value={formData.heure_debut || ''}
                onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heure de fin (optionnel)</label>
              <input
                type="time"
                className="input-field"
                value={formData.heure_fin || ''}
                onChange={(e) => setFormData({ ...formData, heure_fin: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lieu *</label>
              <input
                type="text"
                className="input-field"
                value={formData.lieu || ''}
                onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                required
              />
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Détails de l'événement"
      >
        {selectedEvent && (
          <div className="space-y-6">
            {/* Photo événement */}
            {selectedEvent.photo && (
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <img 
                    src={toMediaUrl(selectedEvent.photo)} 
                    alt={selectedEvent.titre}
                    className="w-full max-w-md h-64 object-cover rounded-lg border-4 border-green-500 shadow-xl"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-green-600 text-white rounded-full p-2 text-xl">
                    📷
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <p className="text-sm text-gray-600 mb-1">Titre</p>
              <p className="font-semibold text-lg text-green-700">{selectedEvent.titre}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Description</p>
              <p className="font-semibold whitespace-pre-wrap break-words bg-gray-50 p-3 rounded-lg">{selectedEvent.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Type</p>
                <p className="font-semibold">{getTypeLabel(selectedEvent.type_event)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Section Hanafia</p>
                <p className="font-semibold">
                  {selectedEvent.section === 'VCN' ? 'VCN (Université)' :
                   selectedEvent.section === 'HDR' ? 'HDR' :
                   selectedEvent.section === 'SANTE' ? 'SANTÉ' :
                   selectedEvent.section}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-semibold">{new Date(selectedEvent.date_event).toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Heure de début</p>
                <p className="font-semibold">{selectedEvent.heure_debut}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Heure de fin</p>
                <p className="font-semibold">{selectedEvent.heure_fin || '-'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Lieu</p>
              <p className="font-semibold">{selectedEvent.lieu}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Statut</p>
              <p className="font-semibold inline-block px-3 py-1 rounded-full bg-green-100 text-green-800">
                {selectedEvent.status_auto === 'a_venir' ? '⏳ À venir' :
                 selectedEvent.status_auto === 'en_cours' ? '🟢 En cours' :
                 selectedEvent.status_auto === 'termine' ? '✅ Terminé' :
                 selectedEvent.status || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Statistiques</p>
              <div className="flex gap-4 mt-2">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">👁️ {selectedEvent.nombre_vues} vues</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full">❤️ {selectedEvent.nombre_likes} likes</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">💬 {selectedEvent.nombre_commentaires || 0} commentaires</span>
              </div>
            </div>

            {/* Section Commentaires */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-bold text-gray-900 mb-4">💬 Commentaires ({selectedEvent.nombre_commentaires || 0})</h3>
              {loadingComments ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-700"></div>
                </div>
              ) : comments.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {(comment.utilisateur.first_name || comment.utilisateur.username || 'U')[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 text-sm">
                              {comment.utilisateur.first_name || comment.utilisateur.username}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(comment.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </div>
                        {comment.approuve ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ Approuvé</span>
                        ) : (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">⏳ En attente</span>
                        )}
                      </div>
                      <p className="text-gray-700 text-sm mt-2 break-words">{comment.contenu}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500">Aucun commentaire pour cet événement</p>
                </div>
              )}
            </div>
            <div className="pt-4 border-t">
              <p className="text-xs text-gray-500">
                Créé le {new Date(selectedEvent.created_at).toLocaleString('fr-FR')}
                {selectedEvent.updated_at && (
                  <> • Modifié le {new Date(selectedEvent.updated_at).toLocaleString('fr-FR')}</>
                )}
              </p>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmer la suppression"
        footer={
          <div className="flex gap-4">
            <button 
              onClick={() => setIsDeleteModalOpen(false)} 
              className="btn-secondary"
              disabled={isDeleting}
            >
              Annuler
            </button>
            <button 
              onClick={confirmDelete} 
              className="btn-danger"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Suppression...
                </>
              ) : (
                'Supprimer'
              )}
            </button>
          </div>
        }
      >
        <p className="text-lg">Êtes-vous sûr de vouloir supprimer l'événement <strong>"{selectedEvent?.titre}"</strong> ?</p>
      </Modal>
    </AdminLayout>
  );
};
